import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useClients, ClientStatus, Client } from '../context/ClientContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faListCheck, faClock, faImage,
  faCodeBranch, faBook, faPalette, faTriangleExclamation, faMagnifyingGlassChart, faListOl, faBullseye, faChartLine, faLink,
} from '@fortawesome/free-solid-svg-icons';
import { faTrello } from '@fortawesome/free-brands-svg-icons';
import ThemeToggle from '../components/ThemeToggle';
import Sidebar from '../components/layout/Sidebar';
import useAvatar from '../hooks/useAvatar';
import { useChat } from '../components/chat/ChatContext';
import { Avatar, Badge, Button, Card, ErrorBoundary, IconButton, Popover, PulseDot, Switch, useToast } from '../components/ui';
import type { BadgeTone } from '../components/ui';
import { formatDate, formatRelativeDate } from '../utils/dateUtils';

// Section components
import TimeClockSection from '../components/clientDetail/sections/TimeClockSection';
import TasksSection from '../components/clientDetail/sections/TasksSection';
import MediaSection from '../components/clientDetail/sections/MediaSection.tsx';
import TagsSection from '../components/clientDetail/sections/TagsSection';
import ReposSection from '../components/clientDetail/sections/ReposSection';
import { SERVICES, fetchClientServices, setClientService, type ClientServices, type ServiceField } from '../lib/clientServices';
import PagesSection from '../components/clientDetail/sections/PagesSection';
import ContentQueueSection from '../components/clientDetail/sections/ContentQueueSection';
import TrelloSection from '../components/clientDetail/sections/TrelloSection';
import GoogleAdsSection from '../components/clientDetail/sections/GoogleAdsSection';
import KnowledgeSection from '../components/clientDetail/sections/KnowledgeSection';
import OffersSection from '../components/clientDetail/sections/OffersSection';
import BacklinksSection from '../components/clientDetail/sections/BacklinksSection';
import CompetenciesCard from '../components/clientDetail/sections/offers/CompetenciesCard';
import { supabase } from '../lib/supabase';

const SECTIONS = [
  { id: 'tasks', label: 'Client Tasks', icon: faListCheck },
  { id: 'timeclock', label: 'Time Clock', icon: faClock },
  { id: 'media', label: 'Media', icon: faImage },
  { id: 'trello', label: 'Trello', icon: faTrello },
  { id: 'repos', label: 'Repos', icon: faCodeBranch },
  { id: 'knowledge', label: 'Knowledge', icon: faBook },
  { id: 'offers', label: 'Offers', icon: faBullseye },
  { id: 'pages', label: 'Pages', icon: faMagnifyingGlassChart },
  { id: 'google-ads', label: 'Google Ads', icon: faChartLine },
  { id: 'backlinks', label: 'Backlinks', icon: faLink },
  { id: 'content-queue', label: 'Content Queue', icon: faListOl },
  { id: 'tags', label: 'Tags', icon: faPalette },
];

// Sections that are one wide table: they take the summary rail's room too.
const WIDE_SECTIONS = new Set(['pages', 'content-queue']);

const CLIENT_STATUS_TONE: Record<ClientStatus, BadgeTone> = {
  Active: 'up',
  Prospect: 'info',
  Paused: 'warn',
  Archived: 'idle',
};

const CLIENT_STATUSES: ClientStatus[] = ['Active', 'Prospect', 'Paused', 'Archived'];

function clientAttention(client: Client): { tone: 'up' | 'warn' | 'info'; label: string } | null {
  const pending = client.tasksDueToday || 0;
  const review = client.tasksAwaitingReview || 0;
  if (pending > 0 && review > 0) return { tone: 'warn', label: 'Partially done — review what is ready' };
  if (pending === 0 && review > 0) return { tone: 'up', label: 'All tasks done — awaiting review' };
  if (pending > 0) return { tone: 'info', label: 'Tasks scheduled for today' };
  return null;
}

/* Status badge that doubles as the existing status-change control. */
function ClientStatusMenu({
  status,
  onChange,
}: {
  status: ClientStatus;
  onChange: (status: ClientStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={buttonRef}
        className="pressable"
        title="Change client status"
        aria-label="Change client status"
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' }}
      >
        <Badge tone={CLIENT_STATUS_TONE[status]} dot>{status}</Badge>
      </button>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        offset={6}
        minWidth={148}
        role="menu"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
          {CLIENT_STATUSES.map((s) => (
            <button
              key={s}
              className="hover-wash pressable"
              onClick={() => {
                setOpen(false);
                if (s !== status) onChange(s);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                padding: '7px 10px',
                border: 'none',
                background: 'transparent',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                fontWeight: (s === status ? 'var(--weight-medium)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
              }}
            >
              <PulseDot tone={CLIENT_STATUS_TONE[s] as 'up' | 'warn' | 'info' | 'idle'} pulse={false} />
              {s}
            </button>
          ))}
      </Popover>
    </span>
  );
}

function SectionChip({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: typeof faListCheck;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className="pressable"
      onClick={onClick}
      data-active={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
        minHeight: 'var(--control-sm)',
        padding: '6px 12px',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid ' + (active ? 'var(--border-strong)' : 'transparent'),
        background: active ? 'var(--surface-active)' : 'transparent',
        color: active ? 'var(--text-body)' : 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--text-sm)',
        fontWeight: (active ? 'var(--weight-medium)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition:
          'background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: '12px', width: '14px' }} />
      {label}
    </button>
  );
}

function SummaryMetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        padding: '9px 0',
        borderBottom: '1px solid var(--border-hairline)',
      }}
    >
      <span className="t-eyebrow">{label}</span>
      <span
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-body)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
        }}
      >
        {children}
      </span>
    </div>
  );
}

/** The services this client buys — the same flags as Settings → Services, for this client only. */
function SummaryServices({ client }: { client: Client }) {
  const toast = useToast();
  const [services, setServices] = useState<ClientServices | null>(null);
  const [saving, setSaving] = useState<ServiceField | null>(null);

  useEffect(() => {
    let cancelled = false;
    setServices(null);
    fetchClientServices(client.id)
      .then((found) => {
        if (!cancelled) setServices(found);
      })
      .catch((err) => console.error('Error fetching client services:', err));
    return () => {
      cancelled = true;
    };
  }, [client.id]);

  const toggle = async (field: ServiceField, next: boolean) => {
    setServices((current) => (current ? { ...current, [field]: next } : current));
    setSaving(field);
    try {
      await setClientService(client.id, field, next);
    } catch (err) {
      console.error('Error updating client service:', err);
      setServices((current) => (current ? { ...current, [field]: !next } : current));
      toast.error(`Couldn’t update ${client.name}`, 'The change was not saved.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      {SERVICES.map(({ field, label }) => (
        <SummaryMetaRow key={field} label={label}>
          <Switch checked={services?.[field] ?? false} disabled={!services || saving === field} onChange={(next) => toggle(field, next)} />
        </SummaryMetaRow>
      ))}
    </>
  );
}

function SummaryRail({ client }: { client: Client }) {
  const pending = client.tasksDueToday || 0;
  const review = client.tasksAwaitingReview || 0;
  const stats = [
    { label: 'Tasks Today', value: pending + review > 0 ? `${review}/${pending + review}` : '—' },
    { label: 'In Progress', value: client.tasks_in_progress > 0 ? String(client.tasks_in_progress) : '—' },
  ];

  return (
    <Card eyebrow="Summary" title={client.name}>
      {client.email ? (
        <div
          className="t-num"
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--text-faint)',
            marginTop: '-10px',
            marginBottom: 'var(--space-4)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {client.email}
        </div>
      ) : null}

      {client.tags.length > 0 ? (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '18px' }}>
          {client.tags.map((t) => (
            <Badge key={t} tone="neutral">{t}</Badge>
          ))}
        </div>
      ) : null}

      {/* mini stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '18px' }}>
        {stats.map((s) => (
          <div
            key={s.label}
            style={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 12px',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '4px' }}>{s.label}</div>
            <div
              className="t-num"
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
              }}
            >
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* meta */}
      <div style={{ borderTop: '1px solid var(--border-hairline)' }}>
        {client.accountOwner ? (
          <SummaryMetaRow label="Owner">
            <Avatar name={client.accountOwner.name} size={20} /> {client.accountOwner.name}
          </SummaryMetaRow>
        ) : null}
        <SummaryMetaRow label="Client Since">
          <span className="t-num">{client.startDate ? formatDate(client.startDate) : '—'}</span>
        </SummaryMetaRow>
        <SummaryMetaRow label="Last Activity">
          <span className="t-num">{client.lastActivityDate ? formatRelativeDate(client.lastActivityDate) : '—'}</span>
        </SummaryMetaRow>
        <SummaryMetaRow label="Next Meeting">
          <span className="t-num">
            {client.nextMeetingDate ? formatDate(client.nextMeetingDate, 'MMM d, h:mm a') : '—'}
          </span>
        </SummaryMetaRow>
        <SummaryServices client={client} />
      </div>
    </Card>
  );
}

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const avatarUrl = useAvatar();
  const { getClientById, isLoading } = useClients();
  const { openChat, setSelectedChannelId } = useChat();
  const [clientChannelId, setClientChannelId] = useState<string | null>(null);

  const [client, setClient] = useState<Client | null>(null);
  // The open section lives in the URL (?section=<id>, ?section=none when every section is closed), so a
  // refresh, a shared link or the back button lands on the same panel. Sections keep their own state in
  // the URL too (Pages: ?page=<id>&show=<filter>); switching section drops it.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedSection = searchParams.get('section');
  const expandedSection =
    wantedSection === 'none' ? null : wantedSection && SECTIONS.some((s) => s.id === wantedSection) ? wantedSection : 'tasks';
  const [error, setError] = useState<string | null>(null);
  const [isLocalLoading, setIsLocalLoading] = useState(true);

  // On phones the section chips are one swipeable row: keep the open one in view (a deep link to
  // ?section=content-queue would otherwise open with its chip scrolled off the right edge).
  const chipStripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = chipStripRef.current;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    const chip = strip.querySelector<HTMLElement>('[data-active="true"]');
    if (!chip) return;
    const s = strip.getBoundingClientRect();
    const c = chip.getBoundingClientRect();
    strip.scrollTo({ left: Math.max(0, strip.scrollLeft + c.left - s.left - (s.width - c.width) / 2), behavior: 'smooth' });
  }, [expandedSection, client]);

  // Helper function to safely execute Supabase queries with retry logic
  const safeSupabaseQuery = async <T,>(queryFn: () => PromiseLike<T>, retries = 3, delay = 1000): Promise<T | undefined> => {
    let attempt = 0;
    while (attempt < retries) {
      try {
        return await queryFn();
      } catch (error) {
        console.error(`Supabase query attempt ${attempt + 1} failed:`, error);
        attempt++;
        if (attempt >= retries) {
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        // Increase delay for next retry (exponential backoff)
        delay *= 2;
      }
    }
  };

  // Fetch client data initially
  useEffect(() => {
    const loadClient = async () => {
      if (!clientId) return;

      try {
        setIsLocalLoading(true);
        setError(null);

        const clientData = await getClientById(clientId);

        if (clientData) {
          setClient(clientData);
        } else {
          // Client not found, redirect to clients page
          setError('Client not found. Redirecting to clients page...');
          setTimeout(() => {
            navigate('/clients');
          }, 2000);
        }
      } catch (error) {
        console.error('Error loading client:', error);
        setError('Failed to load client data. Please try refreshing the page.');
      } finally {
        setIsLocalLoading(false);
      }
    };

    loadClient();
  }, [clientId, getClientById, navigate]);

  // Fetch client chat channel
  useEffect(() => {
    const fetchClientChannel = async () => {
      if (!clientId) return;
      const { data } = await supabase
        .from('chat_channels')
        .select('id')
        .eq('client_id', clientId)
        .eq('type', 'client')
        .limit(1)
        .maybeSingle();
      setClientChannelId(data?.id ?? null);
    };
    fetchClientChannel();
  }, [clientId]);

  const handleOpenChat = () => {
    if (clientChannelId) {
      setSelectedChannelId(clientChannelId);
    }
    openChat();
  };

  // Refresh client data function
  const refreshClientData = async () => {
    if (!clientId) return;

    try {
      setIsLocalLoading(true);
      setError(null);
      const refreshedClient = await getClientById(clientId);

      if (refreshedClient) {
        setClient(refreshedClient);
      } else {
        setError('Failed to refresh client data.');
      }
    } catch (error) {
      console.error('Error refreshing client:', error);
      setError('Failed to refresh client data. Please try again.');
    } finally {
      setIsLocalLoading(false);
    }
  };

  const handleStatusChange = async (status: ClientStatus) => {
    if (!client || !clientId) return;

    try {
      setError(null);

      // Update the client status in the database
      const result = await safeSupabaseQuery(() =>
        supabase.from('clients').update({ status }).eq('id', clientId)
      );

      if (result?.error) {
        console.error('Error updating client status:', result.error);
        setError('Failed to update client status. Please try again.');
        return;
      }

      // Update local client state
      setClient({ ...client, status });
    } catch (error) {
      console.error('Error updating client status:', error);
      setError('Failed to update client status. Network error occurred.');
    }
  };

  const toggleSection = (section: string) => {
    setSearchParams({ section: expandedSection === section ? 'none' : section });
  };

  if (isLoading || isLocalLoading) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--surface-canvas)' }}>
        <Sidebar />
        <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
          <div className="flex items-center justify-center h-[calc(100vh-120px)]">
            <div className="text-center">
              <div
                className="animate-spin w-8 h-8 border-4 rounded-full mx-auto mb-4"
                style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
              />
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Loading client details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--surface-canvas)' }}>
        <Sidebar />
        <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
          <div className="flex items-center justify-center h-[calc(100vh-120px)]">
            <Card style={{ maxWidth: '420px', textAlign: 'center' }}>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={{ color: 'var(--status-warn)', fontSize: '28px', marginBottom: '14px' }}
              />
              <h2
                style={{
                  margin: '0 0 8px',
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                  color: 'var(--text-body)',
                }}
              >
                Error Loading Client
              </h2>
              <p style={{ margin: '0 0 18px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{error}</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <Button onClick={() => refreshClientData()}>Try Again</Button>
                <Button variant="secondary" onClick={() => navigate('/clients')}>Back to Clients</Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--surface-canvas)' }}>
        <Sidebar />
        <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
          <div className="flex items-center justify-center h-[calc(100vh-120px)]">
            <div className="text-center">
              <p
                style={{
                  color: 'var(--text-body)',
                  fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                  marginBottom: '12px',
                }}
              >
                Client not found
              </p>
              <Button onClick={() => navigate('/clients')}>Return to Clients</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const attention = clientAttention(client);
  const isWide = expandedSection != null && WIDE_SECTIONS.has(expandedSection);

  return (
    <div className="min-h-screen" style={{ background: 'var(--surface-canvas)', color: 'var(--text-body)' }}>
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        {/* header */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-4)',
            padding: 'var(--space-5) 0',
            borderBottom: '1px solid var(--border-hairline)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <button
              onClick={() => navigate('/clients')}
              aria-label="Back to Clients"
              title="Back to Clients"
              className="pressable hover-wash"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'var(--control-md)',
                height: 'var(--control-md)',
                borderRadius: 'var(--radius-sm)',
                background: 'transparent',
                border: '1px solid transparent',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '14px' }} />
            </button>
            <Avatar name={client.name} size={30} />
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--text-body)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '9px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name}</span>
              {attention ? <PulseDot tone={attention.tone} title={attention.label} /> : null}
            </h1>
            <ClientStatusMenu status={client.status} onChange={handleStatusChange} />
          </div>

          <div className="hidden md:flex" style={{ alignItems: 'center', gap: '10px' }}>
            <ThemeToggle />
            <IconButton icon="messages-square" label="Open client chat" onClick={handleOpenChat} />
            <IconButton icon="bell" label="Alerts" />
            <Link to="/settings" title="Settings" style={{ display: 'inline-flex' }}>
              <Avatar name={client.accountOwner?.name || 'Me'} src={avatarUrl} size={30} />
            </Link>
          </div>
        </header>

        {/* section chips: one swipeable row on phones (eleven chips wrap to five rows there), wrapped from md up */}
        <div style={{ borderBottom: '1px solid var(--border-hairline)', marginBottom: 'var(--space-6)' }}>
          <div
            ref={chipStripRef}
            className="flex flex-nowrap md:flex-wrap overflow-x-auto md:overflow-x-visible scrollbar-hidden -mx-4 px-4 md:mx-0 md:px-0"
            style={{ gap: '6px', paddingTop: '10px', paddingBottom: '10px' }}
          >
            {SECTIONS.map((s) => (
              <SectionChip
                key={s.id}
                label={s.label}
                icon={s.icon}
                active={expandedSection === s.id}
                onClick={() => toggleSection(s.id)}
              />
            ))}
          </div>
        </div>

        {/* active section + summary rail */}
        <div className={isWide ? 'grid grid-cols-1 gap-5 items-start' : 'grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', minWidth: 0 }}>
            <ErrorBoundary
              key={expandedSection ?? 'none'}
              label={`the ${SECTIONS.find((s) => s.id === expandedSection)?.label ?? 'client'} section`}
            >
              {expandedSection === 'tasks' && (
                <TasksSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                  onTaskAdded={refreshClientData}
                />
              )}

              {expandedSection === 'timeclock' && (
                <TimeClockSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                />
              )}

              {expandedSection === 'media' && (
                <MediaSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                  onMediaAdded={refreshClientData}
                />
              )}

              {expandedSection === 'trello' && (
                <TrelloSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                />
              )}

              {expandedSection === 'repos' && (
                <ReposSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                  onRepoChanged={refreshClientData}
                />
              )}

              {expandedSection === 'knowledge' && (
                <>
                  <CompetenciesCard client={client} />
                  <KnowledgeSection
                    client={client}
                    expandedSection={expandedSection}
                    toggleSection={toggleSection}
                  />
                </>
              )}

              {expandedSection === 'offers' && (
                <OffersSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                />
              )}
              {expandedSection === 'pages' && (
                <PagesSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                />
              )}

              {expandedSection === 'google-ads' && (
                <GoogleAdsSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                />
              )}

              {expandedSection === 'backlinks' && (
                <BacklinksSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                />
              )}

              {expandedSection === 'content-queue' && (
                <ContentQueueSection
                  client={client}
                  expandedSection={expandedSection}
                  toggleSection={toggleSection}
                />
              )}

              {expandedSection === 'tags' && (
                <TagsSection clientId={client.id} tags={client.tags} />
              )}
            </ErrorBoundary>
          </div>

          {isWide ? null : <SummaryRail client={client} />}
        </div>
      </main>
    </div>
  );
}
